import React, { useEffect } from 'react';
import { Loading } from 'design-system-web';
import cx from 'classnames';
import { useChannelAdminContext } from 'hooks';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { routes } from 'lane-shared/config';
import {
  UpdateChargeCodeError,
  useChargeCode,
  useUpdateChargeCode,
} from './hooks';
import { ChargeCodeForm } from './ChargeCodeForm';
import { ChargeCode } from 'domains-billing-web-mappers';
import { AdminPage, PageHeader } from 'components/layout';
import styles from '../styles.scss';

type Props = {
  breadcrumbs?: {
    label: string;
    url?: string;
  }[];
  className?: string;
};

function resolveFormError(
  updateChargeCodeError: unknown,
  error: Error | undefined
): { severity: 'warning' | 'error'; error: Error } | undefined {
  if (updateChargeCodeError instanceof Error) {
    return {
      severity:
        updateChargeCodeError instanceof UpdateChargeCodeError
          ? 'warning'
          : 'error',
      error: updateChargeCodeError,
    };
  }

  if (error) {
    return { severity: 'error', error };
  }

  return undefined;
}

export function EditChargeCodeForm({ breadcrumbs, className }: Props) {
  const history = useHistory();
  const { channel } = useChannelAdminContext();
  const { chargeCodeId }: { chargeCodeId: string } = useParams();
  const { t } = useTranslation();

  const channel_slug = channel?.slug || '';
  const productServicesLink = routes.channelAdminProductsServices.replace(
    ':id',
    channel_slug
  );

  const defaultBreadcrumbs = [
    {
      label: t('adp.productsServices.breadcrumb'),
      url: productServicesLink,
    },
    { label: t('abp.productsServices.editChargeCode.breadcrumb') },
  ];

  const {
    updateChargeCode,
    loading: updating,
    error: updateChargeCodeError,
  } = useUpdateChargeCode();

  const groupId = channel?._id;
  const { chargeCode, loading, error, fetchChargeCode } = useChargeCode({
    chargeCodeId,
    groupId,
  });

  useEffect(() => {
    if (chargeCodeId && groupId) {
      fetchChargeCode();
    }
  }, [chargeCodeId, groupId]);

  const updateChargeCodeDetails = async (data: ChargeCode) => {
    if (!channel?._id) {
      window.Toast.show(
        t('abp.productsServices.editChargeCode.channelNotAvailable'),
        'error'
      );

      return;
    }

    if (!chargeCodeId) {
      window.Toast.show(
        t('abp.productsServices.editChargeCode.chargeCodeIdNotAvailable'),
        'error'
      );

      return;
    }

    try {
      const success = await updateChargeCode({
        chargeCodeId,
        chargeCode: data.chargeCode,
        glCode: data.glCode,
        description: data.description,
        channelId: channel._id,
      });

      if (success) {
        history.goBack();
        window.Toast.show(
          t('abp.productsServices.editChargeCode.successToast')
        );
      }
    } catch (error) {
      console.error('Failed to update charge code', error);
    }
  };

  const handleCancel = () => history.goBack();

  const formError = resolveFormError(updateChargeCodeError, error);

  if (loading && !chargeCode) {
    return (
      <AdminPage className={className}>
        <PageHeader
          header={t('abp.productsServices.editChargeCode.heading')}
          headerLevel="h3"
          breadcrumbs={breadcrumbs ?? defaultBreadcrumbs}
        />
        <div className={styles.centeredContent}>
          <Loading />
        </div>
      </AdminPage>
    );
  }

  return (
    <AdminPage className={className}>
      <PageHeader
        externalPadding={[0, 0]}
        header={t('abp.productsServices.editChargeCode.heading')}
        headerLevel="h3"
        breadcrumbs={breadcrumbs ?? defaultBreadcrumbs}
      />
      <div className={cx(styles.editChargeCodeForm)}>
        <ChargeCodeForm
          chargeCodeData={chargeCode}
          error={formError}
          disabled={updating}
          onSubmit={updateChargeCodeDetails}
          onCancel={handleCancel}
        />
      </div>
    </AdminPage>
  );
}
